<template>
  <div class="app-layout" :class="{ 'kanban-mode': kanbanEnabled }">
    <!-- 20260127 -- Copilot (Claude Sonnet 4.5) -- CreateTaskPopup in AppLayout -->
    <!-- WHY: Sidebar create button needs access to modal -->
    <CreateTaskPopup :task="task" @message:task="makeToast" @reset:task="handleResetTask" />
    <!-- 20260507 -- Copilot (Claude Sonnet 4.6) -- ShareModal in AppLayout so it's reachable from any task list page -->
    <ShareModal ref="shareModal" />
    
    <!-- Premium Upgrade Modal -->
    <PremiumUpgradeModal
      ref="premiumModal"
      :featureName="premiumFeatureName"
      :featureDescription="premiumFeatureDescription"
      :featureBenefits="premiumFeatureBenefits"
      :crossSell="premiumCrossSell"
    />
    
    <!-- 20260615 -- Copilot (Claude Sonnet 4.6) -- Mobile overlay: tap outside to close the sidebar -->
    <div
      v-if="sidebarVisible"
      class="sidebar-mobile-backdrop d-md-none"
      @click="closeSidebar"
      aria-hidden="true"
    />

    <!-- Sidebar Navigation -->
    <aside class="app-sidebar" :class="{ visible: sidebarVisible, collapsed: !sidebarVisible }">
      <div class="sidebar-nav">
        <!-- Create Task - Primary Action -->
        <!-- 20260127 -- Copilot (Claude Sonnet 4.5) -- Handle task creation in AppLayout -->
        <!-- WHY: Sidebar should contain modal instead of each parent page -->
        <div class="sidebar-section sidebar-section-primary">
          <b-button 
            variant="primary" 
            block
            size="sm"
            @click="addTask({}, {})"
            class="sidebar-create-btn"
          >
            <b-icon icon="plus-circle" />
            <span>Create Task</span>
          </b-button>
        </div>

        <!-- 20260128 -- Copilot (Claude Sonnet 4.5) -- Search input in sidebar -->
        <!-- WHY: Always-visible search, redirects to task page if needed -->
        <div class="sidebar-section sidebar-section-search">
          <b-input-group size="sm">
            <template #prepend>
              <b-input-group-text class="search-icon-prepend">
                <b-icon icon="search" font-scale="0.9" />
              </b-input-group-text>
            </template>
            <b-form-input
              v-model="searchTerm"
              placeholder="Search tasks..."
              class="sidebar-search-input"
              @keyup.enter="handleSearch"
              @input="handleSearchInput"
            />
            <template #append v-if="searchTerm">
              <b-button
                size="sm"
                variant="link"
                class="search-clear-btn"
                @click="clearSearch"
              >
                <b-icon icon="x" />
              </b-button>
            </template>
          </b-input-group>
        </div>

        <!-- Quick Access -->
        <div class="sidebar-section">
          <ul class="sidebar-nav">
            <li class="sidebar-nav-item">
              <router-link 
                to="/dashboard"
                active-class="active"
              >
                <b-icon icon="graph-up" />
                <span>Dashboard</span>
              </router-link>
            </li>
            <li class="sidebar-nav-item">
              <a 
                href="#"
                @click.prevent="handleMyTemplatesClick"
              >
                <b-icon icon="folder-fill" />
                <span>My Templates</span>
              </a>
            </li>
            <li class="sidebar-nav-item">
              <router-link
                to="/gallery/public"
                active-class="active"
              >
                <b-icon icon="collection" />
                <span>Public Templates</span>
              </router-link>
            </li>
            <!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- AI Connections moved into Profile > Integrations tab -->
          </ul>
        </div>

        <!-- Quick Task Navigation -->
        <div class="sidebar-section">
          <h3 class="sidebar-section-title">Tasks</h3>
          <ul class="sidebar-nav">
            <li class="sidebar-nav-item">
              <router-link 
                to="/tasks/todays-focus"
                active-class="active"
              >
                <b-icon icon="bullseye" />
                <span>Today's Focus</span>
              </router-link>
            </li>
            <li class="sidebar-nav-item">
              <router-link 
                to="/tasks/this-week"
                active-class="active"
              >
                <b-icon icon="calendar-week" />
                <span>This Week</span>
              </router-link>
            </li>
            <li class="sidebar-nav-item">
              <router-link 
                to="/tasks/overdue"
                active-class="active"
              >
                <b-icon icon="exclamation-circle" />
                <span>Overdue</span>
              </router-link>
            </li>
            <li class="sidebar-nav-item">
              <router-link 
                to="/tasks/get-organized"
                active-class="active"
              >
                <b-icon icon="bookmark" />
                <span>Get Organized</span>
              </router-link>
            </li>
            <li class="sidebar-nav-item">
              <router-link 
                to="/tasks/custom"
                active-class="active"
              >
                <b-icon icon="sliders" />
                <span>Custom</span>
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Folders Section - Collapsible -->
        <div class="sidebar-section sidebar-section-collapsible">
          <h3 
            class="sidebar-section-title sidebar-section-toggle"
            @click="toggleFolders"
          >
            <b-icon :icon="foldersExpanded ? 'chevron-down' : 'chevron-right'" class="toggle-icon" />
            Folders
            <!-- 20260203 -- Copilot (Claude Haiku 4.5) -- Use hash anchor to scroll to Profile page section -->
            <router-link 
              to="/profile#folders"
              class="sidebar-manage-link"
              title="Manage Folders"
              @click.stop
            >
              <b-icon icon="gear" />
            </router-link>
          </h3>
          <b-collapse v-model="foldersExpanded">
            <!-- Debug: Show folder count -->
            <div v-if="folders && folders.length > 0">
              <div v-for="group in groupedFolders" :key="group.name || 'ungrouped'">
                <div v-if="group.name" class="sidebar-group-label">{{ group.name }}</div>
                <ul class="sidebar-nav">
                  <li 
                    v-for="folder in group.folders" 
                    :key="folder.id"
                    class="sidebar-nav-item"
                    :class="{ 'sidebar-nav-item-grouped': group.name }"
                  >
                    <router-link
                      :to="{ name: 'tasks-folder', params: { folder: folder.name } }"
                      active-class="active"
                    >
                      <b-icon icon="folder" />
                      <span>{{ folder.name }}</span>
                    </router-link>
                  </li>
                </ul>
              </div>
            </div>
            <ul v-else class="sidebar-nav">
              <li class="sidebar-nav-item sidebar-empty">
                <span>{{ folders ? 'No folders' : 'Loading folders...' }}</span>
              </li>
            </ul>
          </b-collapse>
        </div>

        <!-- Contexts Section - Collapsible -->
        <div class="sidebar-section sidebar-section-collapsible">
          <h3 
            class="sidebar-section-title sidebar-section-toggle"
            @click="toggleContexts"
          >
            <b-icon :icon="contextsExpanded ? 'chevron-down' : 'chevron-right'" class="toggle-icon" />
            Contexts
            <!-- 20260203 -- Copilot (Claude Haiku 4.5) -- Use hash anchor to scroll to Profile page section -->
            <router-link 
              to="/profile#contexts"
              class="sidebar-manage-link"
              title="Manage Contexts"
              @click.stop
            >
              <b-icon icon="gear" />
            </router-link>
          </h3>
          <b-collapse v-model="contextsExpanded">
            <ul class="sidebar-nav">
              <li 
                v-for="context in contexts" 
                :key="context.id"
                class="sidebar-nav-item"
              >
                <router-link
                  :to="{ name: 'tasks-context', params: { context: context.name } }"
                  active-class="active"
                >
                  <b-icon icon="tag" />
                  <span>{{ context.name }}</span>
                </router-link>
              </li>
              <li v-if="!contexts || contexts.length === 0" class="sidebar-nav-item sidebar-empty">
                <span>No contexts</span>
              </li>
            </ul>
          </b-collapse>
        </div>

        <!-- Slot for additional sidebar content -->
        <slot name="sidebar"></slot>
      </div>
    </aside>

    <!-- Main Content Area -->
    <main class="app-content">
      <!-- Content Header -->
      <div class="app-content-header">
        <div class="content-title">
          <slot name="title">
            <h1 class="display-4">{{ title }}</h1>
            <p class="lead">{{ subtitle }}</p>
          </slot>
        </div>
        <div class="content-controls">
          <!-- 20260203 -- Copilot (Claude Haiku 4.5) -- Sidebar toggle button always available -->
          <!-- WHY: Users need a way to expand sidebar after collapsing it -->
          <!-- 20260615 -- Copilot (Claude Sonnet 4.6) -- d-none d-lg-inline-flex: hamburger in navbar handles mobile/tablet -->
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="toggleSidebar"
            :title="sidebarVisible ? 'Collapse sidebar' : 'Expand sidebar'"
            class="d-none d-lg-inline-flex"
          >
            <b-icon icon="layout-sidebar" />
          </b-button>
          
          <!-- View Toggle Buttons -->
          <b-button-group v-if="showDefaultControls" size="sm">
            <b-button
              @click="handleKanbanToggle"
              variant="outline-secondary"
              :title="kanbanEnabled ? 'Switch to List View' : 'Switch to Kanban View'"
            >
              <b-icon :icon="kanbanEnabled ? 'list-ul' : 'kanban'" />
            </b-button>
            <b-dropdown 
              variant="outline-secondary"
              size="sm"
              right
              title="Group By"
              :disabled="!kanbanEnabled"
            >
              <template #button-content>
                <b-icon icon="grid-3x3-gap" />
              </template>
              <b-dropdown-header>Group By</b-dropdown-header>
              <b-dropdown-item @click="setKanbanGroup('status')">
                <b-icon icon="check-circle" class="me-2" /> Status
              </b-dropdown-item>
              <b-dropdown-item @click="setKanbanGroup('priority')">
                <b-icon icon="arrow-up" class="me-2" /> Priority
              </b-dropdown-item>
              <b-dropdown-item @click="setKanbanGroup('folder')">
                <b-icon icon="folder" class="me-2" /> Folder
              </b-dropdown-item>
              <b-dropdown-item @click="setKanbanGroup('context')">
                <b-icon icon="tag" class="me-2" /> Context
              </b-dropdown-item>
            </b-dropdown>
          </b-button-group>
          
          <slot name="controls"></slot>
        </div>
      </div>

      <!-- Filter Bar -->
      <div class="app-filters" v-if="showFilters">
        <div class="filter-group">
          <label for="filter-priority">Priority:</label>
          <b-form-select 
            id="filter-priority"
            :options="priorityOptions"
            @change="$emit('filter:priority', $event)"
            size="sm"
          ></b-form-select>
        </div>
        <div class="filter-group">
          <label for="filter-status">Status:</label>
          <b-form-select 
            id="filter-status"
            :options="statusOptions"
            @change="$emit('filter:status', $event)"
            size="sm"
          ></b-form-select>
        </div>
        <div class="filter-group">
          <label for="filter-folder">Folder:</label>
          <b-form-select 
            id="filter-folder"
            :options="folderOptions"
            @change="$emit('filter:folder', $event)"
            size="sm"
          ></b-form-select>
        </div>
        <b-button 
          variant="link" 
          size="sm"
          @click="showFilters = false"
          class="ml-auto"
        >
          Close
        </b-button>
      </div>

      <!-- Tasks Container -->
      <div class="app-tasks-container">
        <slot name="content">
          <p class="lead">No content</p>
        </slot>
      </div>

      <!-- Slot for additional content -->
      <slot></slot>
    </main>
  </div>
</template>

<script>
import CreateTaskPopup from '@/components/CreateTaskPopup.vue'
import PremiumUpgradeModal from '@/components/PremiumUpgradeModal.vue'
// 20260507 -- Copilot (Claude Sonnet 4.6) -- Task sharing modal; globally available on all app pages
import ShareModal from '@/components/ShareModal.vue'
import genericOps from '@/mixins/genericOps'
import taskOps from '@/mixins/taskOps'
// 20260615 -- Copilot (Claude Sonnet 4.6) -- Logger for sidebar data errors
import Logger from '@/utils/Logger'

const log = Logger.create('AppLayout', { showCaller: true })

export default {
  name: 'AppLayout',
  components: {
    CreateTaskPopup,
    PremiumUpgradeModal,
    ShareModal,
  },
  mixins: [genericOps, taskOps],
  props: {
    title: {
      type: String,
      default: 'Tasks'
    },
    subtitle: {
      type: String,
      default: ''
    },
    showFiltersDefault: {
      type: Boolean,
      default: false
    },
    showDefaultControls: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      // 20260615 -- Copilot (Claude Sonnet 4.6) -- Start closed on mobile/tablet; sidebar was overlaying content on page load
      sidebarVisible: window.innerWidth >= 992,
      showFilters: this.showFiltersDefault,

      premiumFeatureName: '',
      premiumFeatureDescription: '',
      premiumFeatureBenefits: [],
      premiumCrossSell: '',
      folders: [],
      contexts: [],
      foldersExpanded: localStorage.getItem('sidebarFoldersExpanded') !== 'false',
      contextsExpanded: localStorage.getItem('sidebarContextsExpanded') !== 'false',
      // 20260128 -- Copilot (Claude Sonnet 4.5) -- Search state in AppLayout
      searchTerm: '',
      // 20260127 -- Copilot (Claude Sonnet 4.5) -- Initialize task with emptyTask structure
      // WHY: TaskEntryForm expects task object with all fields, not empty object
      task: {
        "_id": null,
        "id": null,
        "added": null,
        "children": [],
        "completed": null,
        "context": null,
        "due": null,
        "duetime": null,
        "folder": null,
        'length': null,
        "notes": null,
        "owner": null,
        "priority": null,
        "remind": null,
        "remindDate": null,
        "repeat": null,
        "repeatFinal": null,
        "repeatFrom": null,
        "star": null,
        "start": null,
        "starttime": null,
        "status": null,
        "title": null,
        "via": null,
      },
      priorityOptions: [
        { value: null, text: 'All Priorities' },
        { value: 'high', text: 'High' },
        { value: 'medium', text: 'Medium' },
        { value: 'low', text: 'Low' },
        { value: 'neutral', text: 'Neutral' }
      ],
      statusOptions: [
        { value: null, text: 'All Status' },
        { value: 'active', text: 'Active' },
        { value: 'completed', text: 'Completed' }
      ],
      folderOptions: [
        { value: null, text: 'All Folders' },
        { value: 'default', text: 'Default' }
      ]
    }
  },
  async mounted() {
    // Load folders and contexts from store
    await this.loadSidebarData();
    
    // Initialize task with emptyTask for proper Vue reactivity
    this.task = Object.assign({}, this.emptyTask)
    
    // 20260127 -- Copilot (Claude Sonnet 4.5) -- Listen for child task preparation
    // WHY: When child task is created, need to set parent field before modal opens
    this.$root.$on('prepare:childTask', this.handlePrepareChildTask)
    // 20260507 -- Copilot (Claude Sonnet 4.6) -- Open share modal from any task row
    this.$root.$on('open:shareTask', this.handleOpenShareTask)
    // 20260615 -- Copilot (Claude Sonnet 4.6) -- Receive sidebar toggle from mobile navbar hamburger
    this.$root.$on('toggle:sidebar', this.toggleSidebar)
    // 20260616 -- Copilot (Claude Sonnet 4.6) -- Release body scroll lock when viewport resizes across the 768px breakpoint
    // WHY: toggleSidebar only locks at <768px; without this, rotating or resizing to tablet/desktop leaves overflow:hidden permanently
    // 20260619 -- Copilot (Claude Sonnet 4.6) -- Also re-apply lock when resizing back to mobile (<768px) with sidebar open;
    // without this the page stays scrollable behind the off-canvas overlay after a desktop→mobile resize.
    this._handleViewportResize = () => {
      if (window.innerWidth >= 768 && document.body.style.overflow === 'hidden') {
        // Crossed into tablet/desktop — release scroll lock regardless of sidebar state
        document.body.style.overflow = ''
      } else if (window.innerWidth < 768 && this.sidebarVisible && document.body.style.overflow !== 'hidden') {
        // Crossed back into mobile with sidebar already open — re-apply scroll lock
        document.body.style.overflow = 'hidden'
      }
    }
    window.addEventListener('resize', this._handleViewportResize)
  },
  beforeUnmount() {
    // 20260127 -- Copilot (Claude Sonnet 4.5) -- Clean up event listener
    this.$root.$off('prepare:childTask', this.handlePrepareChildTask)
    // 20260507 -- Copilot (Claude Sonnet 4.6) -- Clean up share modal listener
    this.$root.$off('open:shareTask', this.handleOpenShareTask)
    // 20260615 -- Copilot (Claude Sonnet 4.6) -- Clean up sidebar toggle listener
    this.$root.$off('toggle:sidebar', this.toggleSidebar)
    // 20260615 -- Copilot (Claude Sonnet 4.6) -- Release body scroll lock if sidebar was open on unmount
    document.body.style.overflow = ''
    // 20260616 -- Copilot (Claude Sonnet 4.6) -- Clean up viewport resize listener
    window.removeEventListener('resize', this._handleViewportResize)
  },
  watch: {

    '$store.getters.getFoldersForProfile'(newValue) {
      if (newValue) {
        this.folders = newValue.filter(f => f.active === 'Y');
      }
    },
    '$store.getters.getContexts'(newValue) {
      this.contexts = newValue || [];
    },
    // 20260615 -- Copilot (Claude Sonnet 4.6) -- Close sidebar on navigation on mobile/tablet
    $route() {
      if (window.innerWidth < 992) {
        this.closeSidebar()
      }
    },
  },
  computed: {
    // 20260423 -- Copilot (Claude Sonnet 4.6) -- Derived from both pref and premium status so button reflects actual view shown
    kanbanEnabled() {
      return !!(this.$store.getters.getUserPrefs?.displayKanban && this.isPremiumUser())
    },
    // 20260128 -- Copilot (Claude Sonnet 4.5) -- Show search only on task pages
    isTaskPage() {
      return this.$route && this.$route.path && this.$route.path.startsWith('/tasks')
    },
    kanbanGroupLabel() {
      const groupName = this.$store?.getters?.getUserPrefs?.kanbanGroup || 'status';
      const labels = {
        status: 'Group by Status',
        priority: 'Group by Priority',
        folder: 'Group by Folder',
        context: 'Group by Context'
      };
      return labels[groupName] || 'Group by Status';
    },
    groupedFolders() {
      if (!this.folders || this.folders.length === 0) return []

      const groups = {};
      this.folders.forEach(folder => {
        const groupName = folder.group || '';
        if (!groups[groupName]) {
          groups[groupName] = { name: groupName, folders: [] };
        }
        groups[groupName].folders.push(folder);
      });

      return Object.values(groups).sort((a, b) => {
        if (!a.name) return -1;
        if (!b.name) return 1;
        return a.name.localeCompare(b.name);
      });
    }
  },
  methods: {
    toggleFolders() {
      this.foldersExpanded = !this.foldersExpanded
      localStorage.setItem('sidebarFoldersExpanded', this.foldersExpanded)
    },
    toggleContexts() {
      this.contextsExpanded = !this.contextsExpanded
      localStorage.setItem('sidebarContextsExpanded', this.contextsExpanded)
    },
    async loadSidebarData() {
      try {
        // Load folders from store - use FoldersForProfile for flat list
        await this.$store.dispatch('getFolders');
        const allFolders = this.$store.getters.getFoldersForProfile || [];
        this.folders = allFolders.filter(f => f.active === 'Y');

        await this.$store.dispatch('getContexts');
        this.contexts = this.$store.getters.getContexts || [];
      } catch (error) {
        log.error('Error loading sidebar data', error);
      }
    },
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible
      // 20260615 -- Copilot (Claude Sonnet 4.6) -- Prevent body scroll behind mobile sidebar overlay
      if (window.innerWidth < 768) {
        document.body.style.overflow = this.sidebarVisible ? 'hidden' : ''
      }
    },
    // 20260615 -- Copilot (Claude Sonnet 4.6) -- Explicit close for backdrop click and route change
    closeSidebar() {
      this.sidebarVisible = false
      document.body.style.overflow = ''
    },
    handleMyTemplatesClick() {
      // 20260125 -- Copilot (Claude Sonnet 4.5) -- Gate My Templates feature
      // 20260315 -- Copilot (Claude Sonnet 4.6) -- Updated copy: renamed to "Task Templates", new description/benefits/cross-sell
      if (!this.isPremiumUser()) {
        this.premiumFeatureName = 'Task Templates'
        this.premiumFeatureDescription = 'Build your repeatable workflows once \u2014 client onboarding, weekly review, travel packing \u2014 and spin them up in seconds on any device.'
        this.premiumFeatureBenefits = [
          'Build templates once, use them forever',
          'Parent and subtask structure preserved',
          'Share to the Public Gallery',
        ]
        this.premiumCrossSell = 'Plus: Task Velocity analytics, Kanban board view'
        this.$refs.premiumModal.show()
      } else {
        this.$router.push('/gallery/personal')
      }
    },
    handleKanbanToggle() {
      // 20260126 -- Copilot (Claude Sonnet 4.5) -- Allow disabling kanban for non-premium users
      // WHY: Users stuck in kanban view (from expired subscription) need to toggle back to list view
      const isCurrentlyEnabled = this.$store.getters.getUserPrefs.displayKanban
      
      // If currently enabled, allow turning OFF even for non-premium
      if (isCurrentlyEnabled) {
        this.toggleKanban()
      } else {
        // Trying to turn ON - check premium status
        if (!this.isPremiumUser()) {
          this.premiumFeatureName = 'Kanban View'
          this.premiumFeatureDescription = 'Visualize your tasks in a Kanban board grouped by status, priority, folder, or context. Move work forward with a single drag.'
          this.premiumFeatureBenefits = [
            'Group by status, priority, folder, or context',
            'Drag-and-drop organization',
            'Quick status updates',
          ]
          this.premiumCrossSell = 'Plus: Task Velocity analytics, Create private templates'
          this.$refs.premiumModal.show()
        } else {
          this.toggleKanban()
        }
      }
    },
    toggleKanban() {
      // Only update store - the watcher will update local state
      if (this.$store) {
        this.$store.dispatch('toggleKanban');
      }
    },
    setKanbanGroup(groupName) {
      // Update kanban group in store
      if (this.$store) {
        let userPrefs = this.$store.getters.getUserPrefs;
        userPrefs.kanbanGroup = groupName;
        this.$store.dispatch('setUserPrefs', userPrefs);
      }
    },
    // 20260127 -- Copilot (Claude Sonnet 4.5) -- Handle task reset from CreateTaskPopup
    // WHY: Properly reset task object for 'new' and 'similar' actions
    handleResetTask(action) {
      // 20260203 -- Copilot (Claude Haiku 4.5) -- Preserve parent task when creating new subtask
      // WHY: When user clicks "Add & Create New" for a subtask, the next task should also be a subtask
      const currentParent = this.task.parent
      const currentParentTask = this.task.parentTask
      
      if (action === 'new') {
        // Create fresh task object but preserve parent relationship if creating subtasks
        this.task = Object.assign({}, this.emptyTask)
        if (currentParent) {
          this.$set(this.task, 'parent', currentParent)
          this.$set(this.task, 'parentTask', currentParentTask)
          this.$set(this.task, 'folder', currentParentTask?.folder)
        }
      } else if (action === 'similar') {
        // Only clear title, notes, and star
        this.$set(this.task, 'title', '')
        this.$set(this.task, 'notes', '')
        this.$set(this.task, 'star', 'N')
      }
    },
    // 20260127 -- Copilot (Claude Sonnet 4.5) -- Prepare child task with parent info
    // 20260507 -- Copilot (Claude Sonnet 4.6) -- Removed debug console.logs
    // WHY: Set parent field on task before modal opens for child task creation
    handlePrepareChildTask(parentTask) {
      // Start with fresh task
      this.task = Object.assign({}, this.emptyTask)

      // Set parent relationship
      this.$set(this.task, 'parent', parentTask._id)
      this.$set(this.task, 'parentTask', parentTask)
      this.$set(this.task, 'folder', parentTask.folder)
    },
    // 20260507 -- Copilot (Claude Sonnet 4.6) -- Open share modal from any task row via $root event
    handleOpenShareTask(task) {
      this.$refs.shareModal.open(task);
    },
    // 20260128 -- Copilot (Claude Sonnet 4.5) -- Search methods in AppLayout
    // WHY: Emit search events for parent pages to handle filtering
    handleSearchInput() {
      // Clear search if input is empty
      if (!this.searchTerm || this.searchTerm.trim() === '') {
        this.$root.$emit('sidebar:clearSearch')
      }
    },
    handleSearch() {
      if (this.searchTerm && this.searchTerm.trim() !== '') {
        const term = this.searchTerm.trim()
        // Redirect to tasks page if not already there
        if (!this.$route.path.startsWith('/tasks')) {
          // Use query parameter to pass search term
          this.$router.push({ path: '/tasks/custom', query: { search: term } })
        } else {
          this.$root.$emit('sidebar:search', term)
        }
      }
    },
    clearSearch() {
      this.searchTerm = ''
      this.$root.$emit('sidebar:clearSearch')
    }
  }
}
</script>

<style scoped>
/* 20260125 -- Copilot (Claude Sonnet 4.5) -- Premium feature badges */
.premium-badge {
  font-size: 0.7rem;
  margin-left: 4px;
}

.premium-badge-small {
  font-size: 0.6rem;
  position: absolute;
  top: 2px;
  right: 2px;
}

.sidebar-nav-item a.disabled {
  opacity: 0.7;
  cursor: pointer;
}

.sidebar-nav-item a.disabled:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* 20260128 -- Copilot (Claude Sonnet 4.5) -- Clean sidebar header styling */
/* WHY: Button and search as integrated controls with minimal visual treatment */

.sidebar-section-primary {
  padding: 1rem 1rem 0.5rem 1rem;
  margin-bottom: 0;
  border-top: none !important;
}

.sidebar-section-search {
  padding: 0 1rem 0.75rem 1rem;
  margin-bottom: 0.75rem;
  margin-top: 0 !important;
  padding-top: 0 !important;
  border-top: none !important;
}

/* 20260128 -- Copilot (Claude Sonnet 4.5) -- Sidebar collapse tab */
/* WHY: Allow users to collapse sidebar with a vertical tab on the edge */
.sidebar-collapse-tab {
  position: fixed;
  top: 50vh;
  left: calc(var(--spacing-lg) + 240px);
  transform: translateY(-50%);
  width: 22px;
  height: 80px;
  background: white;
  border: 1px solid #ced4da;
  border-left: none;
  border-radius: 0 0.5rem 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  box-shadow: 2px 0 6px rgba(0, 0, 0, 0.1);
  color: #6c757d;
  z-index: 1030;
}

.sidebar-collapse-tab:hover {
  background: #f8f9fa;
  color: #495057;
  width: 26px;
}

/* 20260128 -- Copilot (Claude Sonnet 4.5) -- Hide collapse tab on mobile */
/* WHY: Collapse tab not needed on mobile - sidebar uses different pattern */
@media (max-width: 991.98px) {
  .sidebar-collapse-tab {
    display: none !important;
  }
}

.app-sidebar {
  position: relative;
  transition: all 0.3s ease;
  overflow-y: auto;
  overflow-x: visible !important;
}

.app-sidebar.collapsed {
  width: 0 !important;
  min-width: 0 !important;
  padding: 0 !important;
  border: none !important;
  overflow: hidden;
}

.app-sidebar.collapsed .sidebar-nav {
  display: none;
}

.app-sidebar.collapsed .sidebar-collapse-tab {
  left: var(--spacing-lg);
  border-left: 1px solid #ced4da;
  border-radius: 0.5rem;
}

.sidebar-search-input {
  background-color: #ffffff !important;
  border: 1px solid #ced4da !important;
  color: #495057 !important;
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  transition: all 0.15s ease-in-out;
  height: 31px !important;
  border-radius: 0.2rem !important;
}

.sidebar-search-input::placeholder {
  color: #6c757d !important;
  font-size: 0.875rem;
}

.sidebar-search-input:focus {
  background-color: #ffffff !important;
  border-color: #80bdff !important;
  color: #495057 !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
  outline: none !important;
}

.search-icon-prepend {
  background-color: #e9ecef !important;
  border: 1px solid #ced4da !important;
  border-right: none !important;
  color: #6c757d !important;
  padding: 0.25rem 0.5rem !important;
}

.search-clear-btn {
  color: #6c757d !important;
  padding: 0.25rem 0.5rem !important;
  text-decoration: none !important;
  background: #e9ecef !important;
  border: 1px solid #ced4da !important;
  border-left: none !important;
  transition: all 0.15s ease-in-out;
}

.search-clear-btn:hover {
  color: #495057 !important;
  background: #dee2e6 !important;
  text-decoration: none !important;
}
</style>
